/**
 * VAIR 4-Player Algorithm
 *
 * Based on official VAIR 4-Player Score Card v4.15.25
 *
 * Structure:
 * - 8 total games (2 rounds of 4 games each)
 * - 1 court (matches played sequentially)
 * - All 4 players active in every game
 * - Each player plays 8 games total
 * - Partnerships repeat intentionally (for practice/exposure)
 *
 * Round 1: Games 1-4
 * Round 2: Games 5-8 (different pairings, includes role reversals)
 */
import { VairRound, VairSchedule } from './types';

/**
 * Generate VAIR schedule for 4 players
 * Based on official VAIR 4-Player Score Card
 */
export function generateVairSchedule4Players(timePerMatch: number = 20): VairSchedule {
  let currentTime = 0;
  const rounds: VairRound[] = [];

  // ROUND 1

  // Game 1: 3-4 vs 1-2
  rounds.push({
    matches: [
      {
        team1: { player1: 2, player2: 3 }, // Players 3-4 (0-indexed: 2, 3)
        team2: { player1: 0, player2: 1 }, // Players 1-2 (0-indexed: 0, 1)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [],
  });
  currentTime += timePerMatch;

  // Game 2: 2-3 vs 1-4
  rounds.push({
    matches: [
      {
        team1: { player1: 1, player2: 2 }, // Players 2-3 (0-indexed: 1, 2)
        team2: { player1: 0, player2: 3 }, // Players 1-4 (0-indexed: 0, 3)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [],
  });
  currentTime += timePerMatch;

  // Game 3: 1-3 vs 2-4
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 2 }, // Players 1-3 (0-indexed: 0, 2)
        team2: { player1: 1, player2: 3 }, // Players 2-4 (0-indexed: 1, 3)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [],
  });
  currentTime += timePerMatch;

  // Game 4: 1-4 vs 2-3
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 3 }, // Players 1-4 (0-indexed: 0, 3)
        team2: { player1: 1, player2: 2 }, // Players 2-3 (0-indexed: 1, 2)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [],
  });
  currentTime += timePerMatch;

  // ROUND 2

  // Game 5: 2-4 vs 1-3
  rounds.push({
    matches: [
      {
        team1: { player1: 1, player2: 3 }, // Players 2-4 (0-indexed: 1, 3)
        team2: { player1: 0, player2: 2 }, // Players 1-3 (0-indexed: 0, 2)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [],
  });
  currentTime += timePerMatch;

  // Game 6: 1-2 vs 3-4
  rounds.push({
    matches: [
      {
        team1: { player1: 0, player2: 1 }, // Players 1-2 (0-indexed: 0, 1)
        team2: { player1: 2, player2: 3 }, // Players 3-4 (0-indexed: 2, 3)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [],
  });
  currentTime += timePerMatch;

  // Game 7: 2-3 vs 1-4
  rounds.push({
    matches: [
      {
        team1: { player1: 1, player2: 2 }, // Players 2-3 (0-indexed: 1, 2)
        team2: { player1: 0, player2: 3 }, // Players 1-4 (0-indexed: 0, 3)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [],
  });
  currentTime += timePerMatch;

  // Game 8: 3-4 vs 1-2
  rounds.push({
    matches: [
      {
        team1: { player1: 2, player2: 3 }, // Players 3-4 (0-indexed: 2, 3)
        team2: { player1: 0, player2: 1 }, // Players 1-2 (0-indexed: 0, 1)
        court: 0,
        time: currentTime,
      },
    ],
    byes: [],
  });

  return { rounds };
}

/**
 * Validate 4-player VAIR schedule
 * - Each player should play in exactly 8 games
 * - All 4 players should be active in every game
 */
export function validateVairSchedule4Players(schedule: VairSchedule): boolean {
  const playerGameCount: Map<number, number> = new Map();

  // Initialize
  for (let i = 0; i < 4; i++) {
    playerGameCount.set(i, 0);
  }

  // Count games per player
  schedule.rounds.forEach((round) => {
    round.matches.forEach((match) => {
      const allPlayers = [
        match.team1.player1,
        match.team1.player2,
        match.team2.player1,
        match.team2.player2,
      ];

      allPlayers.forEach((player) => {
        playerGameCount.set(player, (playerGameCount.get(player) || 0) + 1);
      });
    });
  });

  // Validate: Each player should play 8 games
  const expectedGames = 8;
  for (let i = 0; i < 4; i++) {
    const gamesPlayed = playerGameCount.get(i) || 0;
    if (gamesPlayed !== expectedGames) {
      console.error(
        `[4p VAIR] Player ${i + 1} played ${gamesPlayed} games, expected ${expectedGames}`,
      );
      return false;
    }
  }

  // Validate: Should have 8 rounds total
  if (schedule.rounds.length !== 8) {
    console.error(`[4p VAIR] Expected 8 rounds, got ${schedule.rounds.length}`);
    return false;
  }

  // Validate: Each round should have exactly 1 match
  const invalidRounds = schedule.rounds.filter((round) => round.matches.length !== 1);
  if (invalidRounds.length > 0) {
    console.error(
      `[4p VAIR] Expected 1 match per round, found rounds with ${invalidRounds
        .map((r) => r.matches.length)
        .join(', ')} matches`,
    );
    return false;
  }

  return true;
}
